// Where a piece of published content belongs.
//
// An item may be pinned to one topic, in which case students meet it beside
// that topic's notes instead of in a flat list. Course-wide items — a syllabus,
// a timetable — stay unanchored and keep their own destination in the app.
//
// The three ids travel together: the server refuses a topic without the
// section and exam it sits in, because the app could not place it otherwise.

export const NO_TOPIC = '';

/**
 * Every topic in an exam, flattened for a single select.
 *
 * Sections come from useExamHierarchy, each carrying its own topics. The value
 * is the topic id, which is unique across the exam, so one select can offer
 * every topic while still reporting the section it came from.
 */
export function topicChoices(sections) {
  const list = Array.isArray(sections) ? sections : [];
  const choices = [];
  list.forEach((section) => {
    if (!section?.id) return;
    (section.topics || []).forEach((topic) => {
      if (!topic?.id) return;
      const topicTitle = topic.title || topic.name || 'Untitled topic';
      const sectionTitle = section.title || section.name || 'Untitled section';
      choices.push({
        topicId: topic.id,
        sectionId: section.id,
        topicTitle,
        sectionTitle,
        label: `${sectionTitle} — ${topicTitle}`,
      });
    });
  });
  return choices;
}

/**
 * The anchor fields to send for a chosen exam and topic.
 *
 * Returns an empty object when no topic is chosen, so an unanchored item posts
 * nothing rather than nulls.
 */
export function anchorPayload(examId, topicId, sections) {
  if (!examId || !topicId) return {};
  const choice = topicChoices(sections).find((c) => c.topicId === topicId);
  if (!choice) return {};
  return { examId, sectionId: choice.sectionId, topicId: choice.topicId };
}

/** How an item's placement reads in the content table. */
export function anchorLabel(item, sectionsByExam = {}) {
  if (!item?.topicId) return 'Whole course';
  const sections = sectionsByExam[item.examId] || [];
  const choice = topicChoices(sections).find((c) => c.topicId === item.topicId);
  return choice ? choice.label : 'One topic';
}

/** True when the item is pinned to a topic rather than the whole course. */
export function isAnchored(item) {
  return Boolean(item?.topicId);
}
